import React from 'react';

import {
	Select,
	SelectContent,
	SelectItem,
	SelectItemWithDescription,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

import { cn } from '@/lib/utils';

import Label from './Label';

interface Props extends React.PropsWithChildren {
	options: {
		label: string;
		value: string;
		/** When set, item shows label with description below. */
		description?: string;
	}[];
	className?: string;
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	placeholder?: string;
	label?: string;
	labelTooltip?: string;
	containerClassName?: string;
	labelClassName?: string;
	disabled?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** Applied to the open dropdown. Useful with rich options (descriptions) for width caps. */
	contentClassName?: string;
}

const EMPTY_OPTION_VALUE = '_EMPTY_OPTION_VALUE_';

function SimpleSelect({
	options,
	className,
	value,
	defaultValue,
	onValueChange,
	placeholder,
	label,
	labelTooltip,
	containerClassName,
	labelClassName,
	disabled,
	onOpenChange,
	contentClassName,
}: Props) {
	const hasRichOptions = options.some((o) => o.description);

	return (
		<div className={cn('flex w-full flex-col gap-2', containerClassName)}>
			{label && (
				<Label
					tooltip={labelTooltip}
					className={labelClassName}
				>
					{label}
				</Label>
			)}

			<div className="flex-shrink-0">
				<Select
					value={value}
					defaultValue={defaultValue}
					onValueChange={(e) => onValueChange?.(e === EMPTY_OPTION_VALUE ? '' : e)}
					onOpenChange={onOpenChange}
					disabled={disabled}
				>
					<SelectTrigger className={className}>
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent
						className={cn(
							hasRichOptions && 'w-[min(320px,calc(100vw-2rem))]',
							contentClassName
						)}
					>
						{options.map((option) =>
							option.description ? (
								<SelectItemWithDescription
									key={option.value}
									value={option.value ? option.value : EMPTY_OPTION_VALUE}
									label={option.label}
									description={option.description}
									textValue={option.label}
									className="cursor-pointer hover:bg-accent"
								/>
							) : (
								<SelectItem
									key={option.value}
									value={option.value ? option.value : EMPTY_OPTION_VALUE}
									className="cursor-pointer hover:bg-accent"
								>
									{option.label}
								</SelectItem>
							)
						)}
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}

export default SimpleSelect;
